/**
 * Compress and re-encode an image to JPEG so it can be safely sent to the
 * pest recognition edge function on any device, including iPhones that
 * default to HEIC and full-resolution photos that easily exceed 10 MB.
 *
 * Hardened for iOS WebView (Despia / Safari):
 *  - Prefers native `createImageBitmap(file, { resizeWidth, resizeHeight })`
 *    so the browser does the downscale without ever allocating a full-resolution
 *    bitmap in JS heap. This is the single biggest cause of OOM kills that
 *    cause the WebView to reload back to the launch route after capturing
 *    a photo with the system camera.
 *  - Falls back to `<img>` + canvas only if the native path fails.
 *  - Does NOT return a base64 data URL (callers should use `URL.createObjectURL`
 *    for previews and only base64-encode right before the network call).
 */
export interface CompressedImage {
  file: File;
}

const HARD_MAX_BYTES = 25 * 1024 * 1024; // 25 MB safety net on raw input

const SUPPORTED_FALLBACK_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];

export function isLikelyImageFile(file: File): boolean {
  if (file.type && file.type.startsWith("image/")) return true;
  // iOS sometimes returns empty type — fall back to extension check
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return ["jpg", "jpeg", "png", "webp", "heic", "heif"].includes(ext);
}

export function isAcceptedImageType(file: File): boolean {
  if (!file.type) return isLikelyImageFile(file);
  return SUPPORTED_FALLBACK_TYPES.includes(file.type.toLowerCase());
}

/**
 * Read a File/Blob as a base64 string (just the payload, no data URL prefix).
 * Done lazily at submit time so we never carry a giant base64 string in React state.
 */
export function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("FILE_READ_FAILED"));
    reader.readAsDataURL(file);
  });
}

async function nativeResize(
  file: File,
  maxEdge: number,
): Promise<Blob | null> {
  if (typeof createImageBitmap !== "function") return null;
  try {
    // Probe dimensions cheaply first
    const probe = await createImageBitmap(file);
    const longest = Math.max(probe.width, probe.height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const targetW = Math.max(1, Math.round(probe.width * scale));
    const targetH = Math.max(1, Math.round(probe.height * scale));
    probe.close?.();

    // Re-decode at the requested size — this is the key path that avoids
    // allocating a full-resolution bitmap on iOS.
    const bmp = await createImageBitmap(file, {
      resizeWidth: targetW,
      resizeHeight: targetH,
      resizeQuality: "medium",
    } as ImageBitmapOptions);

    try {
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.drawImage(bmp, 0, 0, targetW, targetH);
      return await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.78);
      });
    } finally {
      bmp.close?.();
    }
  } catch {
    return null;
  }
}

async function fallbackResize(
  file: File,
  maxEdge: number,
): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Failed to decode image"));
      el.src = url;
    });
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const targetW = Math.max(1, Math.round(img.naturalWidth * scale));
    const targetH = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("CANVAS_UNAVAILABLE");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("CANVAS_TOBLOB_FAILED"))),
        "image/jpeg",
        0.78,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function compressImageForAnalysis(
  file: File,
  maxEdge = 1024,
): Promise<CompressedImage> {
  if (file.size > HARD_MAX_BYTES) {
    throw new Error("FILE_TOO_LARGE");
  }
  if (!isLikelyImageFile(file)) {
    throw new Error("NOT_AN_IMAGE");
  }

  let blob: Blob | null = await nativeResize(file, maxEdge);
  if (!blob) {
    blob = await fallbackResize(file, maxEdge);
  }
  if (!blob) {
    throw new Error("CANVAS_TOBLOB_FAILED");
  }

  const baseName = (file.name || "photo").replace(/\.[^.]+$/, "");
  const compressedFile = new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  return { file: compressedFile };
}

export async function getAnalysisFile(file: File, maxEdge = 1024): Promise<File> {
  try {
    return (await compressImageForAnalysis(file, maxEdge)).file;
  } catch (err) {
    console.warn("Image compression unavailable; using original capture", err);
    return file;
  }
}
